/* eslint-disable no-restricted-globals */
import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  EmployeeOfferDetails,
  MonetaryDetailsV2,
  PaymentDetails,
} from "../../../../types/Transaction";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../../components/utils";
import { updatePaymentStatus } from "../../../../api/TransactionApisV2";
import { isPaymentAmountComplete } from "../../../../utils/paymentCompletion";

type ApprovePaymentDetailsProps = {
  monetaryDetails: MonetaryDetailsV2;
  onClose: () => void;
  onSuccess?: (data: any) => void;
  transactionId?: string;
  employee?: EmployeeOfferDetails;
  paymentDetails?: PaymentDetails[];
};

const paymentStatusConfig: Record<
  string,
  { label: string; className: string }
> = {
  PAYMENT_PENDING: {
    label: "Pending Approval",
    className: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  },
  SUCCESS: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border border-green-200",
  },
  FAILED: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border border-red-200",
  },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const config = paymentStatusConfig[status] || {
    label: status,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.className}`}
    >
      {config.label}
    </span>
  );
};

const formatDateDisplay = (dateStr: string | Date | undefined): string => {
  if (!dateStr) return "Not provided";
  const d = new Date(dateStr as string);
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isEsopFinancing = (p: PaymentDetails) =>
  p.metaData?.financingType === "ESOP_FINANCING";

const sortByPaymentDate = (tranches: PaymentDetails[]) =>
  [...tranches].sort(
    (a, b) =>
      new Date(a.paymentDate ?? 0).getTime() -
      new Date(b.paymentDate ?? 0).getTime()
  );

// ── Payment table (module-scoped: must not be redefined on every parent
// render, or React remounts its inputs on each keystroke and they lose focus) ──

type PaymentTableProps = {
  tranches: PaymentDetails[];
  isSubmitting: boolean;
  approvingId: string | null;
  rejectingId: string | null;
  dematOverrideForId: string | null;
  overrideReferenceId: string;
  overridePaymentDate: string;
  overrideDematId: string;
  overrideDematType: "NSDL" | "CDSL";
  onOverrideReferenceIdChange: (v: string) => void;
  onOverridePaymentDateChange: (v: string) => void;
  onOverrideDematIdChange: (v: string) => void;
  onOverrideDematTypeChange: (v: "NSDL" | "CDSL") => void;
  onApproveClick: (tranche: PaymentDetails) => void;
  onRejectClick: (tranche: PaymentDetails) => void;
  onConfirmOverrideApproval: (tranche: PaymentDetails) => void;
  onCancelOverride: () => void;
  onDownload: (s3Url: string) => void;
};

const PaymentTable = ({
  tranches,
  isSubmitting,
  approvingId,
  rejectingId,
  dematOverrideForId,
  overrideReferenceId,
  overridePaymentDate,
  overrideDematId,
  overrideDematType,
  onOverrideReferenceIdChange,
  onOverridePaymentDateChange,
  onOverrideDematIdChange,
  onOverrideDematTypeChange,
  onApproveClick,
  onRejectClick,
  onConfirmOverrideApproval,
  onCancelOverride,
  onDownload,
}: PaymentTableProps) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
    <table className="w-full text-sm text-left">
      <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
        <tr>
          <th className="px-4 py-3 font-medium">#</th>
          <th className="px-4 py-3 font-medium">Reference ID</th>
          <th className="px-4 py-3 font-medium">Date</th>
          <th className="px-4 py-3 text-right font-medium">Amount</th>
          <th className="px-4 py-3 font-medium">Status</th>
          <th className="px-4 py-3 font-medium">Financing</th>
          <th className="px-4 py-3 font-medium">Proof</th>
          <th className="px-4 py-3 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {tranches.map((tranche, idx) => (
          <React.Fragment key={tranche.id ?? idx}>
            <tr className="hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
              <td className="px-4 py-3 font-medium text-gray-900">
                {tranche.referenceId || "Not provided"}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {formatDateDisplay(tranche.paymentDate)}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">
                ₹{(tranche.amount ?? 0).toLocaleString("en-IN")}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={tranche.paymentStatus} />
              </td>
              <td className="px-4 py-3">
                {isEsopFinancing(tranche) ? (
                  <VStack className="gap-0.5">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-fit">
                      ESOP Financing
                    </span>
                    {tranche.metaData?.financierName && (
                      <span className="text-xs text-gray-500">
                        {tranche.metaData.financierName}
                      </span>
                    )}
                  </VStack>
                ) : (
                  <span className="text-xs text-gray-400">Self</span>
                )}
              </td>
              <td className="px-4 py-3">
                {tranche.documentLinks && tranche.documentLinks.length > 0 ? (
                  <div className="flex flex-col gap-1.5">
                    {tranche.documentLinks.map((doc, di) =>
                      doc.s3Url ? (
                        <button
                          key={doc.id ?? di}
                          type="button"
                          className="text-primary hover:text-primaryHover hover:underline text-left truncate max-w-[150px] transition-colors focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 -ml-1"
                          onClick={() => onDownload(doc.s3Url!)}
                          title={doc.name || "View"}
                        >
                          {doc.name || `File ${di + 1}`}
                        </button>
                      ) : null
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 italic">No proof</span>
                )}
              </td>
              <td className="px-4 py-3">
                {tranche.paymentStatus === "PAYMENT_PENDING" && tranche.id && (
                  <HStack className="gap-2">
                    <button
                      type="button"
                      onClick={() => onApproveClick(tranche)}
                      disabled={isSubmitting}
                      className="px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 focus:ring-2 focus:ring-green-500 focus:outline-none disabled:opacity-50 transition-colors"
                    >
                      {approvingId === tranche.id ? "…" : "Approve"}
                    </button>
                    <button
                      type="button"
                      onClick={() => onRejectClick(tranche)}
                      disabled={isSubmitting}
                      className="px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 focus:ring-2 focus:ring-red-500 focus:outline-none disabled:opacity-50 transition-colors"
                    >
                      {rejectingId === tranche.id ? "…" : "Reject"}
                    </button>
                  </HStack>
                )}
              </td>
            </tr>
            {dematOverrideForId === tranche.id && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-3 bg-gray-50 border-t border-gray-100"
                >
                  <VStack className="gap-3">
                    <span className="text-xs font-medium text-gray-700">
                      This payment was recorded via ESOP Financing, the employee
                      did not provide transaction details. Fill in what the
                      financier confirmed before approving (defaults to what was
                      recorded on submission).
                    </span>
                    <HStack className="gap-2 items-center">
                      <input
                        type="text"
                        placeholder="Reference ID"
                        value={overrideReferenceId}
                        onChange={(e) =>
                          onOverrideReferenceIdChange(e.target.value)
                        }
                        className="px-2 py-1.5 border border-gray-200 rounded text-sm w-56 focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <input
                        type="date"
                        value={overridePaymentDate}
                        onChange={(e) =>
                          onOverridePaymentDateChange(e.target.value)
                        }
                        className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </HStack>
                    <span className="text-xs font-medium text-gray-700">
                      Override demat account (optional, leave blank to keep the
                      account already recorded for this exercise request; this
                      is for joint demat accounts).
                    </span>
                    <HStack className="gap-2 items-center">
                      <input
                        type="text"
                        placeholder="Demat Account Number"
                        value={overrideDematId}
                        onChange={(e) =>
                          onOverrideDematIdChange(e.target.value)
                        }
                        className="px-2 py-1.5 border border-gray-200 rounded text-sm w-56 focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <select
                        value={overrideDematType}
                        onChange={(e) =>
                          onOverrideDematTypeChange(
                            e.target.value as "NSDL" | "CDSL"
                          )
                        }
                        className="px-2 py-1.5 border border-gray-200 rounded text-sm"
                      >
                        <option value="NSDL">NSDL</option>
                        <option value="CDSL">CDSL</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => onConfirmOverrideApproval(tranche)}
                        disabled={isSubmitting}
                        className="px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
                      >
                        Confirm Approval
                      </button>
                      <button
                        type="button"
                        onClick={onCancelOverride}
                        disabled={isSubmitting}
                        className="px-2.5 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </HStack>
                  </VStack>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const ApprovePaymentDetails = (props: ApprovePaymentDetailsProps) => {
  const {
    monetaryDetails,
    employee,
    onClose,
    onSuccess,
    paymentDetails = [],
  } = props;

  const exercisePaymentLink = monetaryDetails.paymentLink;
  const taxPaymentLink = monetaryDetails.taxPaymentLink;

  const exerciseTranches = sortByPaymentDate(
    paymentDetails.filter(
      (p) => p.paymentType.toLowerCase() === "exercise price"
    )
  );
  const taxTranches = sortByPaymentDate(
    paymentDetails.filter((p) => p.paymentType.toLowerCase() === "tax price")
  );

  const exercisePaymentConfigured = exercisePaymentLink != null;
  const taxPaymentConfigured =
    !!monetaryDetails.taxPaymentEnabled && taxPaymentLink != null;
  const showExerciseSection =
    exercisePaymentConfigured || exerciseTranches.length > 0;
  const showTaxSection = taxPaymentConfigured || taxTranches.length > 0;

  // Totals
  const totalApprovedExercise = exerciseTranches
    .filter((p) => p.paymentStatus === "SUCCESS")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalRequiredExercise =
    (employee as any)?.transactionCompensationDetails?.totalStrikePrice ?? 0;
  const isExerciseFullyPaid = isPaymentAmountComplete(
    totalRequiredExercise,
    totalApprovedExercise
  );

  const totalApprovedTax = taxTranches
    .filter((p) => p.paymentStatus === "SUCCESS")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalRequiredTax =
    (employee as any)?.transactionCompensationDetails?.totalTaxAmount ?? 0;
  const isTaxFullyPaid = isPaymentAmountComplete(
    totalRequiredTax,
    totalApprovedTax
  );

  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const isSubmitting =
    isApprovingAll || isRejecting || !!approvingId || !!rejectingId;

  const [dematOverrideForId, setDematOverrideForId] = useState<string | null>(
    null
  );
  const [overrideDematId, setOverrideDematId] = useState("");
  const [overrideDematType, setOverrideDematType] = useState<"NSDL" | "CDSL">(
    "NSDL"
  );
  const [overrideReferenceId, setOverrideReferenceId] = useState("");
  const [overridePaymentDate, setOverridePaymentDate] = useState("");

  const handleApproveTranche = async (
    tranche: PaymentDetails,
    dematOverride?: { dematAccountId: string; type?: "NSDL" | "CDSL" },
    paymentOverride?: { referenceId?: string; paymentDate?: string }
  ) => {
    if (!tranche.id || !props.transactionId || !employee?.employeeId) return;
    setApprovingId(tranche.id);
    try {
      const res = await updatePaymentStatus({
        transactionId: props.transactionId,
        employeeId: employee.employeeId,
        status: "SUCCESS",
        paymentDetailIds: [tranche.id],
        requestId: employee.requestId,
        dematOverride,
        paymentOverride,
      });
      toast.success("Tranche approved");
      onSuccess?.(res.data);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.errorRaw || "Failed to approve tranche");
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectTranche = async (tranche: PaymentDetails) => {
    if (!tranche.id || !props.transactionId || !employee?.employeeId) return;
    setRejectingId(tranche.id);
    try {
      const res = await updatePaymentStatus({
        transactionId: props.transactionId,
        employeeId: employee.employeeId,
        status: "FAILED",
        paymentDetailIds: [tranche.id],
        requestId: employee.requestId,
      });
      toast.success("Tranche rejected");
      onSuccess?.(res.data);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.errorRaw || "Failed to reject tranche");
    } finally {
      setRejectingId(null);
    }
  };

  const handleApproveClick = (tranche: PaymentDetails) => {
    if (isEsopFinancing(tranche)) {
      setDematOverrideForId(tranche.id!);
      setOverrideDematId("");
      setOverrideDematType("NSDL");
      setOverrideReferenceId(tranche.referenceId || "");
      const d = tranche.paymentDate ? new Date(tranche.paymentDate) : null;
      setOverridePaymentDate(
        d && !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : ""
      );
    } else {
      handleApproveTranche(tranche);
    }
  };

  const handleConfirmOverrideApproval = (tranche: PaymentDetails) => {
    handleApproveTranche(
      tranche,
      overrideDematId
        ? { dematAccountId: overrideDematId, type: overrideDematType }
        : undefined,
      {
        referenceId: overrideReferenceId || undefined,
        paymentDate: overridePaymentDate || undefined,
      }
    );
    setDematOverrideForId(null);
  };

  const handleApproveAllPending = async () => {
    if (!props.transactionId || !employee?.employeeId) return;
    // ESOP-Financing payments must go through the per-row confirm flow (they can
    // carry an optional demat override), so bulk-approve only Self-financed rows.
    const pendingIds = [
      ...exerciseTranches
        .filter(
          (p) =>
            p.paymentStatus === "PAYMENT_PENDING" && p.id && !isEsopFinancing(p)
        )
        .map((p) => p.id!),
      ...taxTranches
        .filter(
          (p) =>
            p.paymentStatus === "PAYMENT_PENDING" && p.id && !isEsopFinancing(p)
        )
        .map((p) => p.id!),
    ];
    const hasEsopPending = [...exerciseTranches, ...taxTranches].some(
      (p) => p.paymentStatus === "PAYMENT_PENDING" && isEsopFinancing(p)
    );
    if (pendingIds.length === 0) {
      toast.error(
        hasEsopPending
          ? "ESOP Financing payments must be approved individually below."
          : "No pending payments to approve"
      );
      return;
    }
    setIsApprovingAll(true);
    try {
      const res = await updatePaymentStatus({
        transactionId: props.transactionId,
        employeeId: employee.employeeId,
        status: "SUCCESS",
        paymentDetailIds: pendingIds,
        requestId: employee.requestId,
      });
      toast.success(
        hasEsopPending
          ? "All pending Self-financed payments approved. ESOP Financing payments must be approved individually."
          : "All pending payments approved"
      );
      onSuccess?.(res.data);
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errorRaw || "Failed to approve payments"
      );
    } finally {
      setIsApprovingAll(false);
    }
  };

  const handleDownload = useCallback((s3Url: string) => {
    if (s3Url) window.open(s3Url, "_blank");
    else toast.error("Failed to get download link");
  }, []);

  const pendingExerciseCount = exerciseTranches.filter(
    (p) => p.paymentStatus === "PAYMENT_PENDING"
  ).length;
  const hasPendingPayments =
    pendingExerciseCount > 0 ||
    taxTranches.some((p) => p.paymentStatus === "PAYMENT_PENDING");

  const paymentTableSharedProps = {
    isSubmitting,
    approvingId,
    rejectingId,
    dematOverrideForId,
    overrideReferenceId,
    overridePaymentDate,
    overrideDematId,
    overrideDematType,
    onOverrideReferenceIdChange: setOverrideReferenceId,
    onOverridePaymentDateChange: setOverridePaymentDate,
    onOverrideDematIdChange: setOverrideDematId,
    onOverrideDematTypeChange: setOverrideDematType,
    onApproveClick: handleApproveClick,
    onRejectClick: handleRejectTranche,
    onConfirmOverrideApproval: handleConfirmOverrideApproval,
    onCancelOverride: () => setDematOverrideForId(null),
    onDownload: handleDownload,
  };

  return (
    <div className="max-w-5xl w-full mx-auto bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
        <div>
          <h6 className="text-lg font-semibold text-gray-900">
            Review Payment Details
          </h6>
          <p className="mt-1 text-sm text-gray-500">
            {employee?.employeeName || "Employee"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded p-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <VStack className="gap-6 p-6">
        {!showExerciseSection && !showTaxSection && (
          <div className="py-12 text-sm text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            No payment details configured for this transaction.
          </div>
        )}

        {/* ── Exercise Payment Section ───────────────────────── */}
        {showExerciseSection && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Exercise Payment
              </span>
              {totalRequiredExercise > 0 && (
                <span className="text-sm text-gray-500">
                  Approved:{" "}
                  <span
                    className={`font-semibold ${
                      isExerciseFullyPaid ? "text-green-600" : "text-primary"
                    }`}
                  >
                    ₹{totalApprovedExercise.toLocaleString("en-IN")}
                  </span>
                  {" / "}₹{totalRequiredExercise.toLocaleString("en-IN")}
                </span>
              )}
            </HStack>

            {/* Progress bar */}
            {totalRequiredExercise > 0 && exerciseTranches.length > 0 && (
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isExerciseFullyPaid ? "bg-green-600" : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (totalApprovedExercise / totalRequiredExercise) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            {exerciseTranches.length > 0 ? (
              <PaymentTable
                tranches={exerciseTranches}
                {...paymentTableSharedProps}
              />
            ) : (
              <div className="text-sm text-gray-400 py-2">
                No payment details yet.
              </div>
            )}
          </VStack>
        )}

        {/* ── Tax Payment Section ───────────────────────────── */}
        {showTaxSection && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Tax Payment
              </span>
              {totalRequiredTax > 0 && (
                <span className="text-sm text-gray-500">
                  Approved:{" "}
                  <span
                    className={`font-semibold ${
                      isTaxFullyPaid ? "text-green-600" : "text-primary"
                    }`}
                  >
                    ₹{totalApprovedTax.toLocaleString("en-IN")}
                  </span>
                  {" / "}₹{totalRequiredTax.toLocaleString("en-IN")}
                </span>
              )}
            </HStack>

            {/* Progress bar */}
            {totalRequiredTax > 0 && taxTranches.length > 0 && (
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isTaxFullyPaid ? "bg-green-600" : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (totalApprovedTax / totalRequiredTax) * 100
                    )}%`,
                  }}
                />
              </div>
            )}

            {taxTranches.length > 0 ? (
              <PaymentTable
                tranches={taxTranches}
                {...paymentTableSharedProps}
              />
            ) : (
              <div className="text-sm text-gray-400 py-2">
                No payment details yet.
              </div>
            )}
          </VStack>
        )}

        {/* ── Footer actions ─────────────────────────────────── */}
        <HStack className="justify-between gap-2 pt-2 border-t border-gray-100">
          <ButtonPrimary1
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </ButtonPrimary1>
          {hasPendingPayments && (
            <HStack className="gap-2">
              <ButtonPrimary1
                type="button"
                onClick={async () => {
                  if (!props.transactionId || !employee?.employeeId) return;
                  const pendingIds = [
                    ...exerciseTranches
                      .filter(
                        (p) => p.paymentStatus === "PAYMENT_PENDING" && p.id
                      )
                      .map((p) => p.id!),
                    ...taxTranches
                      .filter(
                        (p) => p.paymentStatus === "PAYMENT_PENDING" && p.id
                      )
                      .map((p) => p.id!),
                  ];
                  if (pendingIds.length === 0) return;
                  setIsRejecting(true);
                  try {
                    const res = await updatePaymentStatus({
                      transactionId: props.transactionId,
                      employeeId: employee.employeeId,
                      status: "FAILED",
                      paymentDetailIds: pendingIds,
                      requestId: employee.requestId,
                    });
                    toast.success("All pending payments rejected");
                    onSuccess?.(res.data);
                    onClose();
                  } catch (err: any) {
                    toast.error(
                      err?.response?.data?.errorRaw || "Failed to reject"
                    );
                  } finally {
                    setIsRejecting(false);
                  }
                }}
                loading={isRejecting}
                disabled={isSubmitting}
                loadingText="Rejecting..."
              >
                Reject All Pending
              </ButtonPrimary1>
              <ButtonPrimary
                type="button"
                onClick={handleApproveAllPending}
                loading={isApprovingAll}
                disabled={isSubmitting}
                loadingText="Processing..."
              >
                Approve All Pending
              </ButtonPrimary>
            </HStack>
          )}
        </HStack>
      </VStack>
    </div>
  );
};

export default ApprovePaymentDetails;
